<template>
  <div class="sprint-calendar" :class="teamCalendarClass">
    <template v-if="displayWeeks.length">
      <div class="sprint-calendar__header mb-3">
        <button
          type="button"
          class="sprint-calendar__nav-btn"
          :disabled="!canGoPrev"
          :aria-label="rangeMode === 'month' ? 'Previous month' : 'Previous weeks'"
          @click="goPrevPage"
        >
          <i class="fas fa-chevron-left" aria-hidden="true"></i>
        </button>

        <h6 v-if="rangeLabel" class="sprint-calendar__range-title mb-0">
          {{ rangeLabel }}
        </h6>

        <button
          type="button"
          class="sprint-calendar__nav-btn"
          :aria-label="rangeMode === 'month' ? 'Next month' : 'Next weeks'"
          @click="goNextPage"
        >
          <i class="fas fa-chevron-right" aria-hidden="true"></i>
        </button>
      </div>

      <div class="sprint-calendar__grid">
        <div class="sprint-calendar__weekday-row">
          <div
            v-for="(dayLabel, dayIndex) in weekdayLabels"
            :key="dayLabel"
            class="sprint-calendar__weekday"
            :class="{ 'sprint-calendar__weekday--weekend': isWeekendColumn(dayIndex) }"
          >
            {{ dayLabel }}
          </div>
        </div>

        <div
          v-for="(week, weekIndex) in displayWeeks"
          :key="`week-${weekIndex}`"
          class="sprint-calendar__week"
          :class="weekRowClass(week)"
          :style="{ '--lane-count': weekLayouts[weekIndex]?.laneCount || 1 }"
        >
          <div
            v-for="day in week"
            :key="day.key"
            class="sprint-calendar__day"
            :class="dayCellClass(day)"
          >
            <span class="sprint-calendar__day-num">{{ day.date.getDate() }}</span>

            <div v-if="isHoliday(day.date)" class="sprint-calendar__holiday">
              <i class="fas fa-umbrella-beach sprint-calendar__holiday-icon" aria-hidden="true"></i>
              <span class="sprint-calendar__holiday-label">Holiday</span>
              <span v-if="holidayName(day.date)" class="sprint-calendar__holiday-name">{{ holidayName(day.date) }}</span>
            </div>
          </div>

          <div class="sprint-calendar__bars">
            <div
              v-for="bar in weekLayouts[weekIndex]?.bars || []"
              :key="bar.key"
              class="sprint-calendar__event sprint-calendar__bar"
              :class="eventClass(bar.status)"
              :style="{
                gridColumn: `${bar.startCol} / ${bar.endCol}`,
                gridRow: bar.lane,
              }"
              @mouseenter="showBarTooltip($event, bar)"
              @mousemove="moveBarTooltip($event)"
              @mouseleave="hideBarTooltip"
            >
              <span class="sprint-calendar__event-label">{{ bar.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="sprint-calendar__empty text-secondary text-sm">
      No sprint dates to display.
    </div>

    <Teleport to="body">
      <div
        v-if="barTooltip.visible"
        class="sprint-calendar-hover-tip"
        :style="{ left: `${barTooltip.x}px`, top: `${barTooltip.y}px` }"
      >
        <div v-for="(line, index) in barTooltip.lines" :key="index">{{ line }}</div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { testPlanTaskStatusPillClass } from "@/utils/testPlanTaskStatus";
import { getThaiHolidayName, isThaiPublicHoliday } from "@/utils/thaiHolidays";

const WEEKDAY_LABELS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

const TEAM_CALENDAR_CLASS = {
  a: "sprint-calendar--team-a",
  b: "sprint-calendar--team-b",
  c: "sprint-calendar--team-c",
};

function parseDateOnly(val) {
  if (!val) return null;
  const raw = String(val).slice(0, 10);
  const [y, m, d] = raw.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function dateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function startOfWeekSunday(date) {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function buildWeek(weekStart) {
  const cursor = startOfWeekSunday(weekStart);
  const week = [];
  for (let i = 0; i < 7; i += 1) {
    const dayDate = addDays(cursor, i);
    week.push({
      key: dateKey(dayDate),
      date: dayDate,
    });
  }
  return week;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function inferSprintWeekCount(sprintStart, sprintEnd, weekCount) {
  const parsed = Number(weekCount);
  if (Number.isFinite(parsed) && parsed >= 1) return Math.round(parsed);
  if (!sprintStart || !sprintEnd) return 1;
  const ms = sprintEnd.getTime() - sprintStart.getTime();
  const days = Math.max(1, Math.round(ms / 86400000) + 1);
  return Math.max(1, Math.ceil(days / 7));
}

export default {
  name: "TeamSprintCalendar",
  props: {
    team: { type: String, required: true },
    startDate: { type: String, default: null },
    endDate: { type: String, default: null },
    weekCount: { type: Number, default: null },
    rangeMode: { type: String, default: "sprint" },
    tasks: { type: Array, default: () => [] },
    labelSuffix: { type: String, default: "" },
  },
  data() {
    return {
      pageOffset: 0,
      monthCursor: startOfMonth(new Date()),
      barTooltip: { visible: false, x: 0, y: 0, lines: [] },
    };
  },
  watch: {
    startDate() {
      this.pageOffset = 0;
      this.syncMonthCursor();
    },
    endDate() {
      this.pageOffset = 0;
    },
    weekCount() {
      this.pageOffset = 0;
    },
    team() {
      this.pageOffset = 0;
      this.syncMonthCursor();
    },
    rangeMode() {
      this.pageOffset = 0;
      this.syncMonthCursor();
    },
  },
  computed: {
    weekdayLabels() {
      return WEEKDAY_LABELS;
    },
    teamCalendarClass() {
      return TEAM_CALENDAR_CLASS[this.team] || TEAM_CALENDAR_CLASS.a;
    },
    sprintStart() {
      return parseDateOnly(this.startDate);
    },
    sprintEnd() {
      return parseDateOnly(this.endDate);
    },
    resolvedWeekCount() {
      return inferSprintWeekCount(this.sprintStart, this.sprintEnd, this.weekCount);
    },
    displayWeeks() {
      if (this.rangeMode === "month") {
        const monthStart = this.monthCursor || startOfMonth(new Date());
        const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
        const weeks = [];
        let cursor = startOfWeekSunday(monthStart);
        while (cursor <= monthEnd && weeks.length < 6) {
          weeks.push(buildWeek(cursor));
          cursor = addDays(cursor, 7);
        }
        return weeks;
      }
      if (!this.sprintStart) return [];

      const sprintWeekStart = startOfWeekSunday(this.sprintStart);
      const pageStart = addDays(sprintWeekStart, this.pageOffset * this.resolvedWeekCount * 7);

      return [
        buildWeek(pageStart),
        buildWeek(addDays(pageStart, 7)),
      ];
    },
    canGoPrev() {
      if (this.rangeMode === "month") return true;
      return this.pageOffset > 0;
    },
    rangeLabel() {
      if (this.rangeMode === "month") {
        const monthStart = this.monthCursor || startOfMonth(new Date());
        return monthStart.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
      }
      if (!this.displayWeeks.length) return "";
      const start = this.displayWeeks[0][0].date;
      const end = this.displayWeeks[this.displayWeeks.length - 1][6].date;
      const fmt = (d) => d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      return `${fmt(start)} – ${fmt(end)}`;
    },
    normalizedTasks() {
      return (this.tasks || [])
        .map((task) => {
          const start = parseDateOnly(task.startDate);
          const end = parseDateOnly(task.endDate) || start;
          if (!start && !end) return null;
          const rangeStart = start || end;
          const rangeEnd = end || start;
          return {
            taskId: task.taskId,
            title: task.title || `Task ${task.taskId}`,
            status: task.status,
            start: rangeStart,
            end: rangeEnd,
            subProjectName: task.subProjectName || "",
            owners: Array.isArray(task.owners) ? task.owners : [],
          };
        })
        .filter(Boolean);
    },
    weekLayouts() {
      return this.displayWeeks.map((week) => this.buildWeekBars(week));
    },
  },
  methods: {
    syncMonthCursor() {
      const fromSprint = this.sprintStart || parseDateOnly(this.startDate);
      this.monthCursor = startOfMonth(fromSprint || new Date());
    },
    goPrevPage() {
      if (this.rangeMode === "month") {
        const cursor = this.monthCursor || startOfMonth(new Date());
        this.monthCursor = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1);
        return;
      }
      if (!this.canGoPrev) return;
      this.pageOffset -= 1;
    },
    goNextPage() {
      if (this.rangeMode === "month") {
        const cursor = this.monthCursor || startOfMonth(new Date());
        this.monthCursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
        return;
      }
      this.pageOffset += 1;
    },
    isInSprintRange(date) {
      if (!this.sprintStart || !this.sprintEnd) return false;
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d >= this.sprintStart && d <= this.sprintEnd;
    },
    isWeekInSprint(week) {
      return week.some((day) => this.isInSprintRange(day.date));
    },
    weekRowClass(week) {
      return this.isWeekInSprint(week) ? "sprint-calendar__week--sprint" : "sprint-calendar__week--outside";
    },
    isOutsideMonth(date) {
      if (this.rangeMode !== "month") return false;
      const cursor = this.monthCursor || startOfMonth(new Date());
      return date.getMonth() !== cursor.getMonth() || date.getFullYear() !== cursor.getFullYear();
    },
    dayCellClass(day) {
      return {
        "sprint-calendar__day--in-sprint": this.isInSprintRange(day.date),
        "sprint-calendar__day--weekend": this.isWeekend(day.date),
        "sprint-calendar__day--outside-month": this.isOutsideMonth(day.date),
      };
    },
    isWeekendColumn(dayIndex) {
      return dayIndex === 0 || dayIndex === 6;
    },
    isWeekend(date) {
      const day = date.getDay();
      return day === 0 || day === 6;
    },
    isHoliday(date) {
      return isThaiPublicHoliday(date);
    },
    holidayName(date) {
      return getThaiHolidayName(date);
    },
    isSkippedDay(date) {
      return this.isWeekend(date) || this.isHoliday(date);
    },
    buildWeekBars(week) {
      const weekStartKey = week[0].key;
      const weekEndKey = week[6].key;
      const overlapping = this.normalizedTasks
        .filter((task) => dateKey(task.start) <= weekEndKey && dateKey(task.end) >= weekStartKey)
        .sort((a, b) => {
          const byStart = dateKey(a.start).localeCompare(dateKey(b.start));
          if (byStart) return byStart;
          return String(a.taskId ?? "").localeCompare(String(b.taskId ?? ""));
        });

      const items = overlapping.map((task) => {
        const days = new Set();
        week.forEach((day) => {
          if (day.key < dateKey(task.start) || day.key > dateKey(task.end)) return;
          if (this.isSkippedDay(day.date)) return;
          days.add(day.key);
        });
        return { task, days };
      }).filter((item) => item.days.size);

      const laneOccupants = [];
      const taskLane = new Map();
      items.forEach((item) => {
        let lane = 0;
        while (true) {
          const occupants = laneOccupants[lane] || [];
          const conflict = occupants.some((otherDays) => (
            [...item.days].some((day) => otherDays.has(day))
          ));
          if (!conflict) {
            if (!laneOccupants[lane]) laneOccupants[lane] = [];
            laneOccupants[lane].push(item.days);
            taskLane.set(item.task, lane + 1);
            break;
          }
          lane += 1;
        }
      });

      const bars = [];
      items.forEach((item) => {
        const lane = taskLane.get(item.task);
        let startCol = null;
        week.forEach((day, index) => {
          const active = item.days.has(day.key);
          if (active && startCol == null) startCol = index + 1;
          if (!active && startCol != null) {
            bars.push(this.makeWeekBar(item.task, startCol, index + 1, lane, weekStartKey));
            startCol = null;
          }
        });
        if (startCol != null) {
          bars.push(this.makeWeekBar(item.task, startCol, 8, lane, weekStartKey));
        }
      });

      return {
        bars,
        laneCount: Math.max(laneOccupants.length, 1),
      };
    },
    makeWeekBar(task, startCol, endCol, lane, weekStartKey) {
      return {
        key: `${weekStartKey}-${task.taskId}-${startCol}-${endCol}`,
        startCol,
        endCol,
        lane,
        status: task.status,
        title: this.taskLabel(task),
        label: this.taskLabel(task),
        tooltipLines: this.taskTooltipLines(task),
      };
    },
    eventClass(status) {
      return testPlanTaskStatusPillClass(status);
    },
    formatTooltipDate(date) {
      if (!date) return "—";
      const d = date instanceof Date ? date : parseDateOnly(date);
      if (!d) return "—";
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      return `${day}/${month}/${d.getFullYear()}`;
    },
    taskOwnerLabel(task) {
      const owners = Array.isArray(task?.owners) ? task.owners : [];
      const names = owners.map((owner) => String(owner.displayName || "").trim()).filter(Boolean);
      return names.length ? names.join(", ") : "Unassigned";
    },
    taskTooltipLines(task) {
      const prefix = task.taskId != null ? `#${task.taskId}` : "";
      const title = String(task.title || "").trim();
      const line1 = [prefix, title].filter(Boolean).join(" ");
      const line2 = String(task.subProjectName || "").trim() || "—";
      const line3 = this.taskOwnerLabel(task);
      const line4 = `${this.formatTooltipDate(task.start)} - ${this.formatTooltipDate(task.end)}`;
      return [line1, line2, line3, line4];
    },
    showBarTooltip(event, bar) {
      this.barTooltip = {
        visible: true,
        x: event.clientX + 12,
        y: event.clientY + 16,
        lines: Array.isArray(bar?.tooltipLines) ? bar.tooltipLines : [],
      };
    },
    moveBarTooltip(event) {
      if (!this.barTooltip.visible) return;
      this.barTooltip.x = event.clientX + 12;
      this.barTooltip.y = event.clientY + 16;
    },
    hideBarTooltip() {
      this.barTooltip.visible = false;
    },
    taskLabel(task) {
      const prefix = task.taskId != null ? `#${task.taskId}` : "";
      const title = String(task.title || "").trim();
      const head = [prefix, title].filter(Boolean).join(" ");
      if (this.labelSuffix === "subproject") {
        const sub = String(task.subProjectName || "").trim();
        if (sub) return `${head} | ${sub}`;
      }
      if (this.labelSuffix === "owner") {
        return `${head} | ${this.taskOwnerLabel(task)}`;
      }
      return head;
    },
  },
};
</script>

<style scoped>
.sprint-calendar {
  display: flex;
  flex-direction: column;
}

.sprint-calendar__header {
  display: grid;
  grid-template-columns: 2rem 1fr 2rem;
  align-items: center;
  gap: 0.5rem;
}

.sprint-calendar__nav-btn {
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--surface-border, #e9ecef);
  border-radius: 8px;
  background: var(--surface-card, #fff);
  color: var(--text-secondary, #67748e);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.sprint-calendar__nav-btn:hover:not(:disabled) {
  background: var(--primary-light, rgba(52, 71, 103, 0.08));
  color: var(--text-primary, #344767);
  border-color: #d2d6da;
}

.sprint-calendar__nav-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.sprint-calendar__range-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-primary, #344767);
  text-align: center;
}

.sprint-calendar__grid {
  border: 1px solid var(--surface-border, #e9ecef);
  border-radius: var(--radius-card, 12px);
  overflow: hidden;
  background: var(--surface-card, #fff);
}

.sprint-calendar__weekday-row,
.sprint-calendar__week {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.sprint-calendar__week {
  position: relative;
  align-items: stretch;
}

.sprint-calendar__weekday {
  padding: 0.55rem 0.35rem;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-align: center;
  color: var(--text-secondary, #67748e);
  background: #fafbfc;
  border-bottom: 1px solid var(--surface-border, #e9ecef);
}

.sprint-calendar__weekday--weekend {
  background: var(--surface-ground, #f4f6f9);
  color: #8392ab;
}

.sprint-calendar__week--outside .sprint-calendar__day {
  background: var(--surface-ground, #f4f6f9);
  color: var(--text-secondary, #67748e);
  opacity: 0.55;
}

.sprint-calendar__day--weekend {
  background: var(--surface-ground, #f4f6f9) !important;
  opacity: 1;
}

.sprint-calendar__week--outside .sprint-calendar__day--weekend {
  opacity: 0.65;
}

.sprint-calendar--team-a .sprint-calendar__week--sprint .sprint-calendar__day--weekend,
.sprint-calendar--team-b .sprint-calendar__week--sprint .sprint-calendar__day--weekend,
.sprint-calendar--team-c .sprint-calendar__week--sprint .sprint-calendar__day--weekend {
  background: var(--surface-ground, #f4f6f9) !important;
}

.sprint-calendar__day--weekend .sprint-calendar__day-num {
  color: var(--text-secondary, #67748e);
  font-weight: 600;
}

.sprint-calendar__day--outside-month {
  opacity: 0.4;
}

.sprint-calendar--team-a .sprint-calendar__week--sprint .sprint-calendar__day {
  background: rgba(33, 82, 255, 0.06);
}

.sprint-calendar--team-b .sprint-calendar__week--sprint .sprint-calendar__day {
  background: rgba(130, 214, 22, 0.08);
}

.sprint-calendar--team-c .sprint-calendar__week--sprint .sprint-calendar__day {
  background: rgba(251, 99, 64, 0.08);
}

.sprint-calendar__day {
  position: relative;
  min-height: max(5.15rem, calc(2.2rem + var(--lane-count, 1) * 1.28rem + 0.45rem));
  padding: 0.35rem 0.3rem 1.55rem;
  border-right: 1px solid var(--surface-border, #e9ecef);
  border-bottom: 1px solid var(--surface-border, #e9ecef);
  background: var(--surface-card, #fff);
}

.sprint-calendar__day-num {
  position: relative;
  z-index: 3;
  display: inline-block;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-primary, #344767);
  line-height: 1;
  margin-bottom: 0.35rem;
  padding: 0.05rem 0.12rem 0.05rem 0;
}

.sprint-calendar__day:last-child {
  border-right: none;
}

.sprint-calendar__week:last-child .sprint-calendar__day {
  border-bottom: none;
}

.sprint-calendar__day--in-sprint .sprint-calendar__day-num {
  color: var(--text-primary, #344767);
  font-weight: 700;
}

.sprint-calendar__bars {
  position: absolute;
  grid-column: 1 / -1;
  grid-row: 1;
  left: 0;
  right: 0;
  top: 1.95rem;
  bottom: 0.28rem;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  grid-auto-rows: 1.12rem;
  align-content: start;
  row-gap: 0.16rem;
  pointer-events: none;
  z-index: 2;
}

.sprint-calendar__holiday {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
  max-width: calc(100% - 0.4rem);
  pointer-events: none;
}

.sprint-calendar__holiday-icon {
  font-size: 1rem;
  line-height: 1;
  color: #e74c3c;
  flex-shrink: 0;
}

.sprint-calendar__holiday-label {
  font-size: 0.58rem;
  font-weight: 700;
  line-height: 1.2;
  color: #e74c3c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}

.sprint-calendar__holiday-name {
  font-size: 0.5rem;
  font-weight: 600;
  line-height: 1.15;
  color: #e74c3c;
  text-align: center;
  white-space: normal;
  max-width: 100%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.sprint-calendar__event {
  display: block;
  width: 100%;
  padding: 0.12rem 0.3rem;
  border-radius: 4px;
  font-size: 0.58rem;
  font-weight: 600;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-width: 1px;
  border-style: solid;
}

.sprint-calendar__bar {
  display: flex;
  align-items: center;
  min-width: 0;
  margin: 0 2px;
  pointer-events: auto;
}

.sprint-calendar__empty {
  padding: 2rem 0;
  text-align: center;
}
</style>

<style>
.sprint-calendar-hover-tip {
  position: fixed;
  z-index: 4000;
  max-width: 320px;
  padding: 0.45rem 0.65rem;
  border-radius: 8px;
  background: #1f2a37;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.28);
  pointer-events: none;
}
</style>
